import { atom, selector } from "recoil";
import { de } from "const/lang/de";
import { en } from "const/lang/en";
import { zh } from "const/lang/zh";
import { pt } from "const/lang/pt";
import { fr } from "const/lang/fr";
import Services from "services/Services";
import { getLanguages, getBrandFromLocation, getCurrentLanguage } from "util/MagnoliaAppHelpers";
import { isPressBlogUrl } from "util/pressBlogUrl";
import { pagePathState } from "./magnolia";
import { PERSIST_KEY_LANGUAGE } from "./persist";

/**
 * Get initial language for the app.
 * In preview mode: ignore localStorage, use getCurrentLanguage() to get default.
 * In regular mode: use localStorage or fallback to "de".
 */
function getInitialLanguage(): string {
  const urlParams = new URLSearchParams(window.location.search);
  
  // In preview mode, don't use localStorage - use getCurrentLanguage() which handles preview mode properly
  if (urlParams.has("mgnlPreview")) {
    return getCurrentLanguage();
  }
  
  // Regular mode: use localStorage or fallback
  return localStorage.getItem(PERSIST_KEY_LANGUAGE) || "de";
}

export const languageState = atom({
  key: "languageState",
  default: getInitialLanguage(),
});

/**
 * Selector for the urls of the content in all languages
 */
export const contentVariants = selector({
  key: "contentVariants",
  get: async ({ get }) => {
    // all languages that we have => ideally coming from magnolia
    let LANGUAGES = getLanguages();
    let currentLanguage = get(languageState);
    let currentPagePath = get(pagePathState);
    let contentVariants: { [key: string]: string } = {};

    // Get current brand to filter Chinese language for non-ALBIS brands
    const currentBrand = getBrandFromLocation();
    
    // Check if we're in admin context for Portuguese language
    // const isAdmin = isAdminContext();

    // let's check with Magnolia if the page is available in other languages
    for (var i = 0; i < LANGUAGES.length; i++) {
      let languageToCheck = LANGUAGES[i];

      // Skip Chinese language for non-ALBIS brands to prevent 404 backend calls
      if (languageToCheck === "zh" && currentBrand !== "albis") {
        continue;
      }
      
      // Skip Portuguese language for non-ALBIS brands
      if (languageToCheck === "pt" && currentBrand !== "albis") {
        continue;
      }

      // Skip French language for non-ALBIS brands
      if (languageToCheck === "fr" && currentBrand !== "albis") {
        continue;
      }

      if (languageToCheck === currentLanguage) {
        contentVariants[currentLanguage] = currentPagePath;
      } else {
        let pathToCheck = currentPagePath.replace(
          `/${currentLanguage}`,
          `/${languageToCheck}`
        );
        if (pathToCheck.includes("/htf-products/")) {
          contentVariants[languageToCheck] = pathToCheck;
          continue;
          // TODO
          // WHATS HAPPEN HERE?????
          // forces a page reload!
        }

        try {
          // we make a call to the meta data, if successful => it's available
          // Handle both new format (/info-center/{type}/{slug}~{id}) and old format (/info-center/{type}~{name}~s={id}~)
          let pathForMetadata = pathToCheck;
          if (isPressBlogUrl(pathToCheck)) {
            // Extract base path: /info-center/{type} for new format or /{type} for old format
            if (pathToCheck.includes("/info-center/press/")) {
              pathForMetadata = exception(pathToCheck, "/info-center/press");
            } else if (pathToCheck.includes("/info-center/blog/")) {
              pathForMetadata = exception(pathToCheck, "/info-center/blog");
            } else if (pathToCheck.includes("/press~")) {
              pathForMetadata = exception(pathToCheck, "/press");
            } else if (pathToCheck.includes("/blog~")) {
              pathForMetadata = exception(pathToCheck, "/blog");
            }
          }

          const data = await Services.content.loadPageMetaData(pathForMetadata);
          if (data === false) {
            throw new Error("notExists");
          }
          contentVariants[languageToCheck] = pathToCheck;
        } catch (e) {
          // if not => not available, landing page is the preferred destination then
          contentVariants[languageToCheck] = `/${languageToCheck}`;
        }
      }
    }

    return contentVariants;
  },
});

function exception(pagePath: string, identifier: string) {
  const index = pagePath.indexOf(identifier);

  return pagePath.substring(0, index + identifier.length);
}

/**
 * Selector for the translation file corresponding to the current language
 */
export const i18n = selector({
  key: "i18n",
  get: ({ get }) => {
    const currentLanguage = get(languageState);

    switch (currentLanguage) {
      case "de":
        return de;
      case "zh":
        return zh;
      case "pt":
        return pt;
      case "fr":
        return fr;
      default:
        return en;
    }
  },
});

export const getTranslationByKey = selector({
  key: "getTranslationByKey",
  get: () => (key?: string) => {
    switch (key) {
      case "en":
        return en;
      case "zh":
        return zh;
      case "pt":
        return pt;
      case "fr":
        return fr;
      default:
        return de;
    }
  },
});
